import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { currentRelease } from '@/config/releases';
import { isQaSurface } from '@/config/runtime';
import { Clock3, ExternalLink, FileText, GitCommitHorizontal, Rocket } from 'lucide-react';
import { cn } from '@/lib/utils';

type ReleaseNotesButtonProps = {
  className?: string;
  compact?: boolean;
};

export default function ReleaseNotesButton({ className, compact = false }: ReleaseNotesButtonProps) {
  const [open, setOpen] = useState(false);

  if (!isQaSurface) return null;

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size={compact ? 'sm' : 'default'}
        onClick={() => setOpen(true)}
        className={cn(
          'border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 hover:text-violet-800',
          compact ? 'h-8 rounded-full px-3 text-[11px] font-semibold tracking-[0.16em]' : 'font-semibold',
          className,
        )}
        data-testid="release-notes-button"
      >
        <Rocket size={compact ? 13 : 15} />
        <span className="uppercase">Cambio {currentRelease.version}</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl border-[#E2E8F0] bg-white p-0">
          <div className="border-b border-[#E2E8F0] bg-gradient-to-r from-violet-50 via-white to-sky-50 px-6 py-5">
            <DialogHeader className="space-y-3 text-left">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="border-violet-200 bg-violet-100 text-violet-700">
                  QA
                </Badge>
                <Badge variant="outline" className="border-sky-200 bg-sky-50 text-sky-700">
                  Version {currentRelease.version}
                </Badge>
              </div>
              <DialogTitle className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {currentRelease.title}
              </DialogTitle>
              <DialogDescription className="text-sm text-[#475569]">
                {currentRelease.summary}
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="space-y-5 px-6 py-5">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4">
                <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-[#94A3B8]">
                  <Clock3 size={13} />
                  Build
                </div>
                <p className="text-sm font-semibold text-[#0F172A]">{currentRelease.datetime}</p>
                <p className="text-xs text-[#64748B]">{currentRelease.timezone}</p>
              </div>
              <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4">
                <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-[#94A3B8]">
                  <GitCommitHorizontal size={13} />
                  Commit
                </div>
                <p className="text-sm font-semibold text-[#0F172A]">{currentRelease.commit || 'Sin referencia'}</p>
                <p className="text-xs text-[#64748B]">Identificador tecnico del despliegue visible en QA</p>
              </div>
            </div>

            <div>
              <p className="mb-3 text-xs font-bold uppercase tracking-[0.22em] text-[#94A3B8]">Que se hizo</p>
              <div className="space-y-2">
                {currentRelease.items.map((item) => (
                  <div key={item} className="flex items-start gap-3 rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-sm text-[#334155]">
                    <FileText size={15} className="mt-0.5 shrink-0 text-violet-500" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#E2E8F0] pt-4">
              <p className="text-xs text-[#64748B]">
                Use este numero de cambio para validar exactamente que version esta revisando QA.
              </p>
              <Button asChild variant="outline" className="border-[#E2E8F0] text-[#0F172A]">
                <Link to="/changelog" onClick={() => setOpen(false)}>
                  Ver changelog completo
                  <ExternalLink size={14} />
                </Link>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
